import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation, useSearchParams } from "react-router-dom";
import {
  FiBarChart2,
  FiCalendar,
  FiChevronDown,
  FiFileText,
  FiGrid,
  FiLogOut,
  FiMapPin,
  FiMessageSquare,
  FiSettings,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import Navbar from "../Components/Navbar";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useIsMobile from "../hooks/useIsMobile";
import axios from "../api/axiosInstance";
import { readStoredUser, setActiveBusinessUnitSession } from "../utils/sessionUser";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode } from "../demo/demoMode";
import { canAccessReservationsWaitlist } from "../demo/demoAccessPolicy";
import CurrencyQuickSelector from "../currency/CurrencyQuickSelector";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import { canConfigureRms, canViewRms } from "../rms/rmsAccessControl";

function PmsPanel() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [openSection, setOpenSection] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarPinned, setIsSidebarPinned] = useState(false);
  const isMobile = useIsMobile();
  const [businessUnitName, setBusinessUnitName] = useState("");
  const [sessionUser, setSessionUser] = useState(() => readStoredUser());
  const navScrollRef = useRef(null);
  const sectionRefs = useRef(new Map());
  const { themeId, language } = usePmsTheme();
  const { adminEnabled: isMultiCurrencyEnabledByAdmin } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";
  const demoModeActive = isDemoMode();
  const sidebarCopy = language.messages.sidebar;
  const commonCopy = language.messages.common;

  const currentUser = sessionUser || {};
  const allowedModules = Array.isArray(currentUser?.allowed_modules) ? currentUser.allowed_modules : [];
  const permissions = currentUser?.permissions || {};
  const isAdminRole = Number(currentUser?.id) === 1;

  const can = (key) => isAdminRole || Boolean(permissions?.[key]);

  const businessUnitId = demoModeActive
    ? DEMO_BUSINESS_UNIT_ID
    : searchParams.get("business_unit_id") || currentUser?.active_business_unit_id || "1";

  const withBusinessUnit = useCallback(
    (path) => `${path}?business_unit_id=${encodeURIComponent(String(businessUnitId))}`,
    [businessUnitId]
  );

  const canViewReservationsList =
    can("reservations.view_list") || can("reservations.create");
  const canViewReservationsWaitlist = canAccessReservationsWaitlist({
    canViewReservations: canViewReservationsList,
    demoModeActive,
  });
  const canViewDashboard = can("reservations.view_dashboard");
  const canViewReservationsAvailability = can("reservations.view_availability");
  const canViewCategorySalesControl = canViewReservationsAvailability && !demoModeActive;
  const canViewReports = can("reports.view");

  const canViewCheckIns = can("front_desk.view_check_in");
  const canViewCheckOuts = can("front_desk.view_check_out");

  const canViewRoomMatrix = can("rooms.view_matrix");
  const canViewRoomAssignment = can("rooms.view_assignment");
  const canViewRoomBlocks = can("rooms.view_blocks");

  const canViewRevenueManagement = can("revenue.view_room_rates");
  const canAccessRevenueOptimizer = isAdminRole || canViewRms(permissions);
  const canConfigureRevenueOptimizer =
    isAdminRole || canConfigureRms(permissions);
  const canAccessSystemSettings = can("access_settings");
  const canViewReviewIntelligence = can("reviews_intelligence.access");
  const canViewLegalEntitySettings =
    !demoModeActive &&
    (
      canAccessSystemSettings ||
      can("finance.settings.view") ||
      can("finance.settings.manage_legal_entity") ||
      can("finance.settings.manage_taxes")
    );

  const canViewReservationSettings =
    can("hotel_settings.view_customer_settings") ||
    can("hotel_settings.view_stay_settings") ||
    can("hotel_settings.view_reservation_flow") ||
    can("hotel_settings.view_required_fields") ||
    can("hotel_settings.view_required_roles");
  const canViewFrontDeskSettings = can("hotel_settings.view_front_desk_settings");
  const canViewRevenueSettings = can("hotel_settings.view_revenue_settings");
  const hasCurrenciesSettingsPermission =
    isAdminRole || canAccessSystemSettings || canViewRevenueSettings;
  const canViewCurrenciesSettings =
    hasCurrenciesSettingsPermission && isMultiCurrencyEnabledByAdmin;
  const canViewExtrasSettings = can("hotel_settings.view_extras_tab");
  const canViewChargesSettings =
    can("hotel_settings.view_reservation_charges_tab") || (!demoModeActive && canViewExtrasSettings);
  const canViewUsersSettings = isAdminRole || can("hotel_settings.view_users_tab");
  const canViewBookingEngineSettings =
    isAdminRole || can("hotel_settings.view_booking_engine_tab");
  const canViewAppearanceSettings =
    canViewReservationSettings ||
    canViewRevenueSettings ||
    canViewExtrasSettings ||
    canViewChargesSettings ||
    canViewUsersSettings ||
    canViewBookingEngineSettings;

  const canAccessPms =
    allowedModules.includes("pms") &&
    (
      canViewDashboard ||
      canViewReservationsList ||
      canViewReservationsAvailability ||
      canViewCategorySalesControl ||
      canViewReports ||
      canViewCheckIns ||
      canViewCheckOuts ||
      canViewRoomMatrix ||
      canViewRoomAssignment ||
      canViewRoomBlocks ||
      canViewRevenueManagement ||
      canAccessRevenueOptimizer ||
      canViewReviewIntelligence ||
      canViewLegalEntitySettings ||
      canViewReservationSettings ||
      canViewFrontDeskSettings ||
      canViewRevenueSettings ||
      canViewCurrenciesSettings ||
      canViewExtrasSettings ||
      canViewChargesSettings ||
      canViewBookingEngineSettings
    );

  const isSidebarExpanded = isSidebarPinned || isSidebarOpen;

  const handleSidebarEnter = () => {
    if (!isSidebarExpanded) {
      setOpenSection(null);
    }
    setIsSidebarOpen(true);
  };

  const handleSidebarLeave = () => {
    if (isSidebarPinned) return;
    setIsSidebarOpen(false);
    setOpenSection(null);
  };

  const handlePinToggle = () => {
    setIsSidebarPinned((prev) => {
      const next = !prev;
      if (next) {
        if (!isSidebarExpanded) {
          setOpenSection(null);
        }
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
        setOpenSection(null);
      }
      return next;
    });
  };

  const setSectionRef = useCallback((key, node) => {
    if (node) {
      sectionRefs.current.set(key, node);
      return;
    }
    sectionRefs.current.delete(key);
  }, []);

  useEffect(() => {
    const syncSessionFromStorage = () => {
      setSessionUser(readStoredUser());
    };

    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const syncActiveBusinessUnit = async () => {
      const requestedBu = Number(businessUnitId || 0);
      const activeBu = Number(currentUser?.active_business_unit_id || 0);

      if (!currentUser?.id || !requestedBu || requestedBu === activeBu) {
        return;
      }

      try {
        const nextUser = await setActiveBusinessUnitSession(currentUser.id, requestedBu);
        if (!cancelled) {
          setSessionUser(nextUser);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[PmsPanel] Error syncing active business unit:", err);
        }
      }
    };

    syncActiveBusinessUnit();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId, currentUser?.active_business_unit_id, currentUser?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadBusinessUnitName = async () => {
      if (!businessUnitId) return;
      try {
        const res = await axios.get(`/api/business-units/${businessUnitId}`);
        if (!cancelled) {
          setBusinessUnitName(String(res?.data?.name || "").trim());
        }
      } catch (err) {
        if (!cancelled) {
          setBusinessUnitName("");
        }
        console.warn("[PmsPanel] Error loading business unit name:", err);
      }
    };

    loadBusinessUnitName();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId]);

  useEffect(() => {
    if (!isSidebarExpanded || !openSection) {
      return undefined;
    }

    const navElement = navScrollRef.current;
    const sectionElement = sectionRefs.current.get(openSection);

    if (!navElement || !sectionElement) {
      return undefined;
    }

    let frameId = 0;
    let timeoutId = 0;

    const ensureSectionVisible = () => {
      if (!navScrollRef.current || !sectionRefs.current.has(openSection)) {
        return;
      }

      const currentNav = navScrollRef.current;
      const currentSection = sectionRefs.current.get(openSection);

      if (!currentNav || !currentSection) {
        return;
      }

      const navRect = currentNav.getBoundingClientRect();
      const sectionRect = currentSection.getBoundingClientRect();
      const safePadding = 12;
      const overflowBottom = sectionRect.bottom - navRect.bottom;
      const overflowTop = navRect.top - sectionRect.top;

      if (overflowBottom > -safePadding) {
        currentNav.scrollTo({
          top: currentNav.scrollTop + overflowBottom + safePadding,
          behavior: "smooth",
        });
        return;
      }

      if (overflowTop > safePadding) {
        currentNav.scrollTo({
          top: Math.max(0, currentNav.scrollTop - overflowTop - safePadding),
          behavior: "smooth",
        });
      }
    };

    frameId = window.requestAnimationFrame(() => {
      ensureSectionVisible();
      timeoutId = window.setTimeout(ensureSectionVisible, 170);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [isSidebarExpanded, openSection]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("palantir_token");
    localStorage.removeItem("palantir_user");
    window.location.href = "/";
  }, []);

  const menuItems = useMemo(() => {
    const reservationsChildren = [
      canViewReservationsList && {
        key: "list",
        label: sidebarCopy.listOfReservations,
        to: withBusinessUnit("/reservations/list"),
      },
      canViewReservationsWaitlist && {
        key: "waitlist",
        label: sidebarCopy.waitlist,
        to: withBusinessUnit("/reservations/waitlist"),
      },
      canViewReservationsAvailability && {
        key: "availability",
        label: sidebarCopy.reservationsAvailability,
        to: withBusinessUnit("/reservations/availability"),
      },
      canViewCategorySalesControl && {
        key: "allocations",
        label: sidebarCopy.categorySalesControl,
        to: withBusinessUnit("/reservations/allocations"),
      },
    ].filter(Boolean);

    const frontDeskChildren = [
      canViewCheckIns && {
        key: "check-ins",
        label: sidebarCopy.checkInsToday,
        to: withBusinessUnit("/front-desk/check-ins"),
      },
      canViewCheckOuts && {
        key: "in-house",
        label: sidebarCopy.inHouseGuests,
        to: withBusinessUnit("/front-desk/in-house"),
      },
      canViewCheckOuts && {
        key: "check-outs",
        label: sidebarCopy.checkOutsToday,
        to: withBusinessUnit("/front-desk/check-outs"),
      },
    ].filter(Boolean);

    const reportsChildren = [
      canViewReports && {
        key: "monthly-stay-performance",
        label: sidebarCopy.monthlyStayPerformance,
        to: withBusinessUnit("/reports/monthly-stay-performance"),
      },
    ].filter(Boolean);

    const roomChildren = [
      canViewRoomMatrix && {
        key: "matrix",
        label: sidebarCopy.roomMatrix,
        to: withBusinessUnit("/rooms-management/matrix"),
      },
      canViewRoomAssignment && {
        key: "assignment",
        label: sidebarCopy.roomAssignment,
        to: `${withBusinessUnit("/reservations/list")}&view=grid&grid_view=room_numbers`,
      },
      canViewRoomBlocks && {
        key: "blocks",
        label: sidebarCopy.roomBlocks,
        to: withBusinessUnit("/rooms-management/blocks"),
      },
    ].filter(Boolean);

    const revenueChildren = [
      canViewRevenueManagement && {
        key: "rates-management",
        label: sidebarCopy.roomRatesManagement,
        to: withBusinessUnit("/revenue-management-container/management"),
      },
      canViewRevenueManagement && {
        key: "rates-matrix",
        label: sidebarCopy.roomRatesMatrix,
        to: withBusinessUnit("/revenue-management-container/matrix"),
      },
      canViewRevenueManagement && !demoModeActive && {
        key: "market-intelligence",
        label: sidebarCopy.marketIntelligence,
        children: [
          {
            key: "rules",
            label: sidebarCopy.marketRulesMapping,
            to: withBusinessUnit("/revenue-management-container/market-intelligence/rules"),
          },
          {
            key: "competitor-prices",
            label: sidebarCopy.competitorPriceMonitor,
            to: withBusinessUnit(
              "/revenue-management-container/market-intelligence/competitor-prices"
            ),
          },
          {
            key: "internal-vs-market",
            label: sidebarCopy.internalVsMarketRatePrices,
            to: withBusinessUnit(
              "/revenue-management-container/market-intelligence/internal-vs-market"
            ),
          },
        ],
      },
      canAccessRevenueOptimizer && {
        key: "revenue-optimizer",
        label: sidebarCopy.revenueOptimizer,
        children: [
          canConfigureRevenueOptimizer && {
            key: "optimizer-setup",
            label: sidebarCopy.revenueOptimizerSetup,
            to: withBusinessUnit("/revenue-management-container/revenue-optimizer/setup"),
          },
          canAccessRevenueOptimizer && {
            key: "optimizer-signals",
            label: sidebarCopy.revenueOptimizerSignalsForecast,
            to: withBusinessUnit(
              "/revenue-management-container/revenue-optimizer/signals-forecast"
            ),
          },
          canAccessRevenueOptimizer && {
            key: "optimizer-market",
            label: sidebarCopy.revenueOptimizerMarketBenchmark,
            to: withBusinessUnit(
              "/revenue-management-container/revenue-optimizer/market-benchmark"
            ),
          },
          canAccessRevenueOptimizer && {
            key: "optimizer-recommendations",
            label: sidebarCopy.revenueOptimizerRecommendations,
            to: withBusinessUnit(
              "/revenue-management-container/revenue-optimizer/recommendations"
            ),
          },
        ].filter(Boolean),
      },
    ].filter(Boolean);

    const settingsChildren = [
      canViewLegalEntitySettings && {
        key: "legal-entity-settings",
        label: sidebarCopy.hotelLegalDetails || "Hotel & Legal Details",
        to: withBusinessUnit("/settings/legal-entity"),
      },
      canViewReservationSettings && {
        key: "reservation-settings",
        label: sidebarCopy.reservationSettings,
        to: withBusinessUnit("/settings/reservation"),
      },
      canViewFrontDeskSettings && {
        key: "front-desk-settings",
        label: sidebarCopy.frontDeskSettings,
        to: withBusinessUnit("/settings/front-desk"),
      },
      canViewRevenueSettings && {
        key: "revenue-settings",
        label: sidebarCopy.revenueSettings,
        to: withBusinessUnit("/settings/revenue"),
      },
      canViewCurrenciesSettings && {
        key: "currencies",
        label: sidebarCopy.currencies || "Currencies",
        to: withBusinessUnit("/settings/currencies"),
      },
      canViewExtrasSettings && {
        key: "extras",
        label: sidebarCopy.extrasDiscounts,
        to: withBusinessUnit("/settings/extras"),
      },
      canViewChargesSettings && {
        key: "charges",
        label: sidebarCopy.reservationCharges,
        to: withBusinessUnit("/settings/charges"),
      },
      canViewUsersSettings && {
        key: "users",
        label: sidebarCopy.usersRoles,
        to: withBusinessUnit("/settings/users"),
      },
      canViewAppearanceSettings && {
        key: "appearance",
        label: sidebarCopy.userExperience,
        to: withBusinessUnit("/settings/appearance"),
      },
      canViewBookingEngineSettings && {
        key: "booking-engine",
        label: sidebarCopy.bookingEngine,
        to: withBusinessUnit("/settings/booking-engine"),
      },
    ].filter(Boolean);

    const reviewIntelligenceChildren = [
      canViewReviewIntelligence && {
        key: "dashboard",
        label: sidebarCopy.reviewsDashboard,
        to: withBusinessUnit("/reviews-intelligence/dashboard"),
      },
      canViewReviewIntelligence && {
        key: "operations",
        label: sidebarCopy.reviewOperations,
        to: withBusinessUnit("/reviews-intelligence/operations"),
      },
      canViewReviewIntelligence && {
        key: "trends-actions",
        label: sidebarCopy.trendsActions,
        to: withBusinessUnit("/reviews-intelligence/trends-actions"),
      },
      canViewReviewIntelligence && {
        key: "all-reviews",
        label: sidebarCopy.allReviews,
        to: withBusinessUnit("/reviews-intelligence/all-reviews"),
      },
      canViewReviewIntelligence && {
        key: "review-import",
        label: sidebarCopy.reviewImport,
        to: withBusinessUnit("/reviews-intelligence/review-import"),
      },
      canViewReviewIntelligence && {
        key: "guest-feedback-surveys",
        label: sidebarCopy.guestFeedbackSurveys,
        to: withBusinessUnit("/reviews-intelligence/surveys"),
      },
    ].filter(Boolean);

    return [
      canViewDashboard && {
        key: "dashboard",
        label: sidebarCopy.dashboard,
        meta: sidebarCopy.dashboardMeta,
        to: withBusinessUnit("/reservations/dashboard"),
        Icon: FiBarChart2,
      },
      reservationsChildren.length
        ? {
            key: "reservations",
            label: sidebarCopy.reservations,
            meta: sidebarCopy.reservationsMeta,
            activePrefix: "/reservations",
            Icon: FiCalendar,
            children: reservationsChildren,
          }
        : null,
      frontDeskChildren.length
        ? {
            key: "front-desk",
            label: sidebarCopy.frontDesk,
            meta: sidebarCopy.frontDeskMeta,
            activePrefix: "/front-desk",
            Icon: FiUsers,
            children: frontDeskChildren,
          }
        : null,
      roomChildren.length
        ? {
            key: "rooms-management",
            label: sidebarCopy.roomsManagement,
            meta: sidebarCopy.roomsManagementMeta,
            activePrefix: "/rooms-management",
            Icon: FiGrid,
            children: roomChildren,
          }
        : null,
      revenueChildren.length
        ? {
            key: "revenue-management",
            label: sidebarCopy.revenueManagement,
            meta: sidebarCopy.revenueManagementMeta,
            activePrefix: "/revenue-management-container",
            Icon: FiTrendingUp,
            children: revenueChildren,
          }
        : null,
      reviewIntelligenceChildren.length
        ? {
            key: "review-intelligence",
            label: sidebarCopy.reviewIntelligence,
            meta: sidebarCopy.reviewIntelligenceMeta,
            activePrefix: "/reviews-intelligence",
            Icon: FiMessageSquare,
            children: reviewIntelligenceChildren,
          }
        : null,
      reportsChildren.length
        ? {
            key: "reports",
            label: sidebarCopy.reports,
            meta: sidebarCopy.reportsMeta,
            activePrefix: "/reports",
            Icon: FiFileText,
            children: reportsChildren,
          }
        : null,
      settingsChildren.length
        ? {
            key: "settings",
            label: sidebarCopy.settings,
            meta: sidebarCopy.settingsMeta,
            activePrefix: "/settings",
            Icon: FiSettings,
            children: settingsChildren,
          }
        : null,
    ].filter(Boolean);
  }, [
    canViewDashboard,
    canAccessRevenueOptimizer,
    canConfigureRevenueOptimizer,
    canViewBookingEngineSettings,
    canViewAppearanceSettings,
    canViewCategorySalesControl,
    canViewCheckIns,
    canViewCheckOuts,
    canViewChargesSettings,
    canViewExtrasSettings,
    canViewFrontDeskSettings,
    canViewLegalEntitySettings,
    canViewReservationSettings,
    canViewReservationsAvailability,
    canViewReservationsList,
    canViewReservationsWaitlist,
    canViewReports,
    canViewReviewIntelligence,
    canViewRevenueManagement,
    canViewRevenueSettings,
    canViewCurrenciesSettings,
    canViewRoomAssignment,
    canViewRoomBlocks,
    canViewRoomMatrix,
    canViewUsersSettings,
    demoModeActive,
    sidebarCopy,
    withBusinessUnit,
  ]);

  const getPathMatchScore = (to) => {
    const [path, rawQuery = ""] = String(to || "").split("?");
    const pathMatches =
      location.pathname === path || location.pathname.startsWith(`${path}/`);

    if (!path || !pathMatches) return -1;

    const targetParams = new URLSearchParams(rawQuery);
    const routeDiscriminators = Array.from(targetParams.entries()).filter(
      ([key]) => key !== "business_unit_id"
    );
    const currentParams = new URLSearchParams(location.search);
    const queryMatches = routeDiscriminators.every(
      ([key, value]) => currentParams.get(key) === value
    );

    if (!queryMatches) return -1;

    const exactPathBonus = location.pathname === path ? 10000 : 5000;
    const querySpecificityBonus = routeDiscriminators.length * 1000;
    return exactPathBonus + querySpecificityBonus + path.length;
  };

  const isPathActive = (to) => getPathMatchScore(to) >= 0;

  const getNodeActiveScore = (node) => {
    if (node.to) return getPathMatchScore(node.to);
    if (node.children?.length) {
      return Math.max(...node.children.map((child) => getNodeActiveScore(child)));
    }
    return -1;
  };

  const isNodeActive = (node) => getNodeActiveScore(node) >= 0;

  let activeMenuKey = null;
  let activeMenuScore = -1;

  menuItems.forEach((item) => {
    let score = getNodeActiveScore(item);

    if (
      item.key === "reservations" &&
      location.pathname.startsWith("/reservation-group")
    ) {
      score = Math.max(score, 10000 + "/reservation-group".length);
    }

    if (
      score < 0 &&
      item.activePrefix &&
      (location.pathname === item.activePrefix ||
        location.pathname.startsWith(`${item.activePrefix}/`))
    ) {
      score = 1000 + item.activePrefix.length;
    }

    if (score > activeMenuScore) {
      activeMenuKey = item.key;
      activeMenuScore = score;
    }
  });

  const renderSubNode = (
    node,
    depth = 1,
    parentKey = "root",
    rootMenuKey = null
  ) => {
    const nodeKey = `${parentKey}-${node.key}`;

    if (node.children?.length) {
      const activeGroup = activeMenuKey === rootMenuKey && isNodeActive(node);
      return (
        <div
          key={nodeKey}
          className={`pmsSubGroup pmsSubGroup--depth${depth} ${
            activeGroup ? "pmsSubGroup--active" : ""
          }`}
        >
          <div className="pmsSubGroupLabel">{node.label}</div>
          <div className="pmsSubGroupChildren">
            {node.children.map((child) =>
              renderSubNode(child, depth + 1, nodeKey, rootMenuKey)
            )}
          </div>
        </div>
      );
    }

    const subActive = activeMenuKey === rootMenuKey && isPathActive(node.to);

    return (
      <Link
        key={nodeKey}
        to={node.to}
        title={node.label}
        aria-current={subActive ? "page" : undefined}
        className={`pmsSubItem pmsSubItem--depth${Math.min(depth, 3)} ${
          subActive ? "pmsSubItem--active" : ""
        }`}
      >
        {node.label}
      </Link>
    );
  };

  const uiCss = `
    .pmsShell {
      min-height: 100dvh;
      width: 100%;
      display: flex;
      gap: 16px;
      padding: 14px;
      box-sizing: border-box;
      background:
        radial-gradient(1100px 520px at 8% -10%, rgba(48, 216, 255, 0.10), transparent 62%),
        radial-gradient(900px 520px at 90% 0%, rgba(168, 85, 247, 0.12), transparent 58%),
        radial-gradient(1100px 680px at 40% 110%, rgba(255, 77, 79, 0.07), transparent 62%);
    }

    .pmsCurrencyControl {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .pmsCurrencyControl__trigger {
      width: 100%;
      min-height: 32px;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 8px;
      border: 1px solid rgba(124, 231, 255, 0.18);
      border-radius: 10px;
      color: rgba(237, 250, 255, 0.92);
      background: rgba(48, 216, 255, 0.045);
      cursor: pointer;
      text-align: left;
      transition: background 120ms ease, border-color 120ms ease;
    }

    .pmsCurrencyControl__trigger:hover,
    .pmsCurrencyControl--open .pmsCurrencyControl__trigger {
      background: rgba(48, 216, 255, 0.09);
      border-color: rgba(124, 231, 255, 0.28);
    }

    .pmsCurrencyControl__trigger:disabled {
      opacity: 0.55;
      cursor: wait;
    }

    .pmsCurrencyControl__code {
      min-width: 38px;
      height: 22px;
      display: grid;
      place-items: center;
      border-radius: 7px;
      background: rgba(255, 255, 255, 0.07);
      font-size: 0.72rem;
      font-weight: 900;
      letter-spacing: 0.04em;
    }

    .pmsCurrencyControl__text {
      min-width: 0;
      flex: 1 1 auto;
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 8px;
      font-size: 0.74rem;
      font-weight: 800;
      opacity: 0;
      max-width: 0;
      overflow: hidden;
      white-space: nowrap;
      transition: opacity 160ms ease, max-width 160ms ease;
    }

    .pmsCurrencyControl__chevron {
      display: grid;
      place-items: center;
      color: rgba(230, 230, 247, 0.55);
      opacity: 0;
      max-width: 0;
      overflow: hidden;
      transition: opacity 160ms ease, max-width 160ms ease, transform 160ms ease;
    }

    .pmsCurrencyControl--open .pmsCurrencyControl__chevron {
      transform: rotate(180deg);
    }

    .pmsSidebar--open .pmsCurrencyControl__text,
    .pmsSidebar--open .pmsCurrencyControl__chevron,
    .pmsMobileCurrency .pmsCurrencyControl__text,
    .pmsMobileCurrency .pmsCurrencyControl__chevron {
      opacity: 1;
      max-width: 220px;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsCurrencyControl__trigger {
      justify-content: center;
      padding-left: 0;
      padding-right: 0;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsCurrencyControl__code {
      min-width: 44px;
    }

    .pmsCurrencyControl__options {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 5px;
      padding: 5px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 10px;
      background: rgba(0, 0, 0, 0.16);
    }

    .pmsCurrencyControl__option {
      min-height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      border: 1px solid transparent;
      border-radius: 7px;
      color: rgba(230, 230, 247, 0.76);
      background: transparent;
      font-size: 0.7rem;
      font-weight: 850;
      cursor: pointer;
    }

    .pmsCurrencyControl__option:hover,
    .pmsCurrencyControl__option--active {
      color: #effcff;
      border-color: rgba(48, 216, 255, 0.22);
      background: rgba(48, 216, 255, 0.09);
    }

    .pmsMobileCurrency {
      margin: 0 0 10px;
      max-width: 280px;
    }

    .pmsMain--mobile {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .pmsMain--mobile > .pmsMobileCurrency {
      flex: 0 0 auto;
    }

    .pmsMain--mobile > :not(.pmsMobileCurrency) {
      flex: 1 1 auto;
      min-height: 0;
      height: auto;
    }

    @media (max-width: 768px) {
      .pmsShell {
        padding: 10px;
        gap: 10px;
      }
    }

    .pmsSidebar {
      position: sticky;
      top: 14px;
      height: calc(100vh - 28px);
      width: 96px;
      flex: 0 0 auto;
      border-radius: 18px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
        linear-gradient(135deg, rgba(48, 216, 255, 0.55), rgba(168, 85, 247, 0.65), rgba(255, 77, 79, 0.40)) border-box;
      box-shadow: 0 28px 80px rgba(0, 0, 0, 0.55);
      overflow: hidden;
      transition: width 160ms ease;
    }

    .pmsSidebar::before {
      content: "";
      position: absolute;
      inset: 0;
      background:
        radial-gradient(520px 220px at 20% 0%, rgba(48, 216, 255, 0.12), transparent 60%),
        radial-gradient(420px 220px at 90% 10%, rgba(168, 85, 247, 0.14), transparent 62%);
      pointer-events: none;
      opacity: 0.95;
    }

    .pmsSidebar > * {
      position: relative;
      z-index: 1;
    }

    .pmsSidebar--open {
      width: 328px;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsNavItemText {
      display: none;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsChevron {
      display: none;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsBrand {
      gap: 0;
      justify-content: center;
      padding-left: 0;
      padding-right: 0;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsBrandText {
      display: none;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsBrandMark {
      margin: 0 auto;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsNav {
      width: calc(100% + 8px);
      margin-right: -8px;
      padding-left: 0;
      padding-right: 0;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsNav > * {
      width: calc(100% - 8px);
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsNavItem {
      justify-content: center;
      padding-left: 0;
      padding-right: 0;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsNavItemIcon {
      margin: 0 auto;
    }

    .pmsSidebarInner {
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 12px 8px;
      gap: 10px;
      overflow: hidden;
    }

    .pmsBrand {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 8px;
      border-radius: 14px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(255, 255, 255, 0.03);
      user-select: none;
    }

    .pmsBrandMark {
      width: 40px;
      height: 40px;
      min-width: 40px;
      min-height: 40px;
      flex: 0 0 40px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      padding: 6px;
      border: 1px solid rgba(48, 216, 255, 0.35);
      background: radial-gradient(circle at 20% 20%, rgba(48, 216, 255, 0.18), rgba(168, 85, 247, 0.20));
      color: rgba(230, 230, 247, 0.92);
      font-weight: 900;
      letter-spacing: 0.4px;
      box-shadow: 0 18px 40px rgba(48, 216, 255, 0.08);
    }

    .pmsBrandLogo {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.38));
    }

    .pmsBrandText {
      display: flex;
      flex-direction: column;
      gap: 1px;
      overflow: hidden;
      opacity: 0;
      max-width: 0;
      transition: opacity 160ms ease, max-width 160ms ease;
    }

    .pmsSidebar--open .pmsBrandText {
      opacity: 1;
      max-width: 195px;
    }

    .pmsBrandTitle {
      font-weight: 900;
      color: transparent;
      background: linear-gradient(
        90deg,
        #e6e6f7 0%,
        rgba(48, 216, 255, 0.95) 45%,
        rgba(168, 85, 247, 0.95) 95%
      );
      -webkit-background-clip: text;
      background-clip: text;
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .pmsBrandSubtitle {
      font-size: 0.72rem;
      color: rgba(230, 230, 247, 0.55);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .pmsNav {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      gap: 6px;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
      padding-top: 2px;
      padding-right: 4px;
      padding-bottom: 8px;
      scrollbar-width: thin;
      scrollbar-color: rgba(230, 230, 247, 0.16) transparent;
      scroll-behavior: smooth;
      overscroll-behavior: contain;
    }

    .pmsNav::-webkit-scrollbar {
      width: 4px;
    }

    .pmsNav::-webkit-scrollbar-track {
      background: transparent;
    }

    .pmsNav::-webkit-scrollbar-thumb {
      background: rgba(230, 230, 247, 0.16);
      border-radius: 999px;
    }

    .pmsNav:hover::-webkit-scrollbar-thumb {
      background: rgba(230, 230, 247, 0.24);
    }

    .pmsNavSection {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    .pmsNavItem {
      width: 100%;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.02);
      color: rgba(230, 230, 247, 0.88);
      text-decoration: none;
      cursor: pointer;
      transition: transform 120ms ease, background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 8px;
      padding: 10px 8px;
      text-align: left;
    }

    .pmsNavItem:hover {
      background: rgba(48, 216, 255, 0.06);
      border-color: rgba(48, 216, 255, 0.18);
      transform: translateY(-1px);
      box-shadow: 0 18px 40px rgba(0, 0, 0, 0.25);
    }

    .pmsNavItem--active {
      background: rgba(168, 85, 247, 0.12);
      border-color: rgba(168, 85, 247, 0.25);
      box-shadow: 0 18px 50px rgba(168, 85, 247, 0.10);
    }

    .pmsNavItemIcon {
      width: 40px;
      height: 40px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.10);
      flex: 0 0 auto;
      color: rgba(230, 230, 247, 0.92);
      box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
    }

    .pmsNavItem--active .pmsNavItemIcon {
      border-color: rgba(48, 216, 255, 0.28);
      background: radial-gradient(circle at 20% 20%, rgba(48, 216, 255, 0.14), rgba(168, 85, 247, 0.16));
      box-shadow: 0 18px 40px rgba(48, 216, 255, 0.08);
    }

    .pmsNavItemText {
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
      flex: 1 1 auto;
    }

    .pmsNavItemLabel,
    .pmsNavItemMeta,
    .pmsChevron {
      opacity: 0;
      max-width: 0;
      overflow: hidden;
      transition: opacity 160ms ease, max-width 160ms ease, transform 160ms ease;
    }

    .pmsNavItemLabel {
      font-weight: 850;
      font-size: 0.95rem;
      color: rgba(230, 230, 247, 0.92);
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .pmsNavItemMeta {
      font-size: 0.74rem;
      color: rgba(230, 230, 247, 0.55);
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .pmsChevron {
      margin-left: auto;
      color: rgba(230, 230, 247, 0.55);
      display: grid;
      place-items: center;
    }

    .pmsNavItem[aria-expanded="true"] .pmsChevron {
      transform: rotate(180deg);
    }

    .pmsSidebar--open .pmsNavItemLabel,
    .pmsSidebar--open .pmsNavItemMeta,
    .pmsSidebar--open .pmsChevron {
      opacity: 1;
      max-width: 280px;
    }

    .pmsSidebar--open .pmsChevron {
      max-width: 28px;
    }

    .pmsSubNav {
      margin-left: 8px;
      margin-right: 6px;
      border-left: 1px solid rgba(255, 255, 255, 0.08);
      padding-left: 12px;
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      transition: max-height 180ms ease, opacity 180ms ease;
    }

    .pmsSubNav--open {
      max-height: 960px;
      overflow: visible;
      opacity: 1;
      padding-top: 6px;
      padding-bottom: 8px;
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsSubNav {
      max-height: 0;
      opacity: 0;
      padding-top: 0;
      padding-bottom: 0;
      pointer-events: none;
    }

    .pmsSubItem {
      display: flex;
      align-items: center;
      gap: 10px;
      border-radius: 12px;
      padding: 9px 10px;
      margin: 4px 0;
      color: rgba(230, 230, 247, 0.82);
      text-decoration: none;
      border: 1px solid rgba(255, 255, 255, 0.06);
      background: rgba(255, 255, 255, 0.02);
      transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
    }

    .pmsSubItem:hover {
      background: rgba(48, 216, 255, 0.05);
      border-color: rgba(48, 216, 255, 0.16);
      transform: translateY(-1px);
    }

    .pmsSubItem--active {
      background: rgba(48, 216, 255, 0.08);
      border-color: rgba(48, 216, 255, 0.22);
      color: rgba(230, 230, 247, 0.92);
    }

    .pmsSubGroup {
      margin: 5px 0 7px;
      border: 1px solid rgba(255, 255, 255, 0.06);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.02);
      padding: 6px 8px;
    }

    .pmsSubGroup--active {
      border-color: rgba(48, 216, 255, 0.22);
      background: rgba(48, 216, 255, 0.05);
    }

    .pmsSubGroupLabel {
      font-size: 0.76rem;
      font-weight: 800;
      color: rgba(230, 230, 247, 0.86);
      letter-spacing: 0.1px;
      margin-bottom: 4px;
      text-transform: none;
    }

    .pmsSubGroupChildren {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .pmsSubItem--depth2 {
      margin-left: 6px;
      font-size: 0.8rem;
      border-style: dashed;
      border-color: rgba(255, 255, 255, 0.10);
      background: rgba(255, 255, 255, 0.015);
      padding-top: 7px;
      padding-bottom: 7px;
    }

    .pmsSubItem--depth3 {
      margin-left: 14px;
      font-size: 0.77rem;
      border-style: dashed;
      border-color: rgba(255, 255, 255, 0.11);
      background: rgba(255, 255, 255, 0.02);
      padding-top: 6px;
      padding-bottom: 6px;
    }

    .pmsBrandHotel {
      font-size: 0.67rem;
      color: rgba(206, 219, 243, 0.54);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 1px;
      line-height: 1.15;
    }

    .pmsBrandActions {
      margin-top: 3px;
      display: flex;
      align-items: center;
      gap: 7px;
    }

    .pmsBrandLogout {
      border: 0;
      background: transparent;
      color: rgba(233, 240, 252, 0.56);
      font-size: 0.68rem;
      font-weight: 700;
      line-height: 1;
      padding: 0;
      cursor: pointer;
      text-decoration: underline;
      text-decoration-color: rgba(233, 240, 252, 0.32);
      text-underline-offset: 2px;
      transition: color 120ms ease, text-decoration-color 120ms ease;
    }

    .pmsBrandLogout:hover {
      color: rgba(233, 240, 252, 0.86);
      text-decoration-color: rgba(233, 240, 252, 0.62);
    }

    .pmsBrandLogoutIcon {
      color: rgba(233, 240, 252, 0.46);
      display: grid;
      place-items: center;
      width: 12px;
      height: 12px;
    }

    .pmsPinButton {
      margin-left: auto;
      width: 28px;
      height: 28px;
      border-radius: 10px;
      border: 1px solid rgba(255, 255, 255, 0.12);
      background: rgba(255, 255, 255, 0.04);
      color: rgba(230, 230, 247, 0.75);
      display: grid;
      place-items: center;
      cursor: pointer;
      transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
    }

    .pmsPinButton:hover {
      background: rgba(48, 216, 255, 0.1);
      border-color: rgba(48, 216, 255, 0.28);
      color: rgba(230, 230, 247, 0.95);
    }

    .pmsPinButton--active {
      background: rgba(168, 85, 247, 0.18);
      border-color: rgba(168, 85, 247, 0.4);
      color: rgba(230, 230, 247, 0.98);
    }

    .pmsMain {
      flex: 1 1 auto;
      min-width: 0;
      padding: 0 4px;
      height: calc(100dvh - 28px);
      overflow: clip;
    }

    .pmsMain,
    .pmsMain * {
      scrollbar-width: thin;
      scrollbar-color: rgba(109, 208, 235, 0.72) rgba(255, 255, 255, 0.08);
    }

    .pmsMain *::-webkit-scrollbar {
      width: 7px;
      height: 7px;
    }

    .pmsMain *::-webkit-scrollbar-thumb {
      background: linear-gradient(180deg, rgba(109, 208, 235, 0.9), rgba(37, 154, 211, 0.9));
      border-radius: 999px;
    }

    .pmsMain *::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.08);
      border-radius: 999px;
    }

  `;

  const themeCss = isLightTheme
    ? `
        .pmsShell {
          background:
            radial-gradient(1120px 520px at 8% -10%, rgba(33, 89, 104, 0.10), transparent 60%),
            radial-gradient(980px 560px at 92% 0%, rgba(167, 124, 49, 0.08), transparent 58%),
            radial-gradient(1160px 700px at 40% 110%, rgba(85, 119, 99, 0.08), transparent 64%),
            linear-gradient(180deg, #efeae2 0%, #f6f2eb 54%, #ece6dc 100%);
        }

        .pmsSidebar {
          background:
            linear-gradient(180deg, rgba(251, 248, 241, 0.97), rgba(240, 234, 224, 0.97)) padding-box,
            linear-gradient(135deg, rgba(33, 89, 104, 0.28), rgba(167, 124, 49, 0.18), rgba(85, 119, 99, 0.18)) border-box;
          box-shadow: 0 28px 76px rgba(90, 69, 39, 0.16);
        }

        .pmsSidebar::before {
          background:
            radial-gradient(520px 240px at 16% 0%, rgba(33, 89, 104, 0.08), transparent 60%),
            radial-gradient(420px 220px at 88% 8%, rgba(167, 124, 49, 0.08), transparent 62%);
        }

        .pmsBrand {
          border-color: rgba(24, 54, 69, 0.08);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.76), rgba(246, 240, 231, 0.72));
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
        }

        .pmsCurrencyControl__trigger {
          border-color: rgba(33, 89, 104, 0.14);
          color: #183645;
          background: rgba(255, 255, 255, 0.62);
        }

        .pmsCurrencyControl__trigger:hover,
        .pmsCurrencyControl--open .pmsCurrencyControl__trigger {
          border-color: rgba(33, 89, 104, 0.24);
          background: rgba(255, 255, 255, 0.9);
        }

        .pmsCurrencyControl__code,
        .pmsCurrencyControl__options {
          background: rgba(24, 54, 69, 0.055);
        }

        .pmsCurrencyControl__chevron {
          color: rgba(69, 78, 81, 0.66);
        }

        .pmsCurrencyControl__option {
          color: rgba(24, 54, 69, 0.74);
        }

        .pmsCurrencyControl__option:hover,
        .pmsCurrencyControl__option--active {
          color: #183645;
          border-color: rgba(33, 89, 104, 0.2);
          background: rgba(33, 89, 104, 0.08);
        }

        .pmsBrandMark {
          border-color: rgba(33, 89, 104, 0.20);
          background: radial-gradient(circle at 25% 20%, rgba(33, 89, 104, 0.18), rgba(167, 124, 49, 0.10));
          color: #183645;
          box-shadow: 0 16px 32px rgba(33, 89, 104, 0.12);
        }

        .pmsBrandTitle {
          background: linear-gradient(90deg, #183645 0%, #215968 48%, #9a7531 100%);
          -webkit-background-clip: text;
          background-clip: text;
        }

        .pmsBrandSubtitle,
        .pmsBrandHotel,
        .pmsNavItemMeta,
        .pmsChevron,
        .pmsBrandLogout,
        .pmsBrandLogoutIcon {
          color: rgba(69, 78, 81, 0.68);
        }

        .pmsBrandLogout {
          text-decoration-color: rgba(69, 78, 81, 0.26);
        }

        .pmsBrandLogout:hover {
          color: #183645;
          text-decoration-color: rgba(24, 54, 69, 0.44);
        }

        .pmsPinButton {
          border-color: rgba(24, 54, 69, 0.10);
          background: rgba(255, 255, 255, 0.68);
          color: rgba(24, 54, 69, 0.72);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.74);
        }

        .pmsPinButton:hover {
          background: rgba(255, 255, 255, 0.92);
          border-color: rgba(33, 89, 104, 0.18);
          color: #183645;
        }

        .pmsPinButton--active {
          background: linear-gradient(180deg, rgba(33, 89, 104, 0.14), rgba(167, 124, 49, 0.10));
          border-color: rgba(33, 89, 104, 0.24);
          color: #183645;
        }

        .pmsNavItem,
        .pmsSubItem,
        .pmsSubGroup {
          border-color: rgba(24, 54, 69, 0.08);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.80), rgba(244, 239, 230, 0.74));
          color: #183645;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.66);
        }

        .pmsNavItem:hover,
        .pmsSubItem:hover {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(241, 234, 223, 0.92));
          border-color: rgba(33, 89, 104, 0.18);
          box-shadow: 0 16px 28px rgba(90, 69, 39, 0.10);
        }

        .pmsNavItem--active {
          background: linear-gradient(180deg, rgba(33, 89, 104, 0.14), rgba(167, 124, 49, 0.08));
          border-color: rgba(33, 89, 104, 0.22);
          box-shadow: 0 18px 32px rgba(33, 89, 104, 0.10);
        }

        .pmsNavItemIcon {
          background: rgba(255, 255, 255, 0.78);
          border-color: rgba(24, 54, 69, 0.08);
          color: #183645;
          box-shadow: none;
        }

        .pmsNavItem--active .pmsNavItemIcon {
          border-color: rgba(33, 89, 104, 0.22);
          background: radial-gradient(circle at 20% 20%, rgba(33, 89, 104, 0.16), rgba(167, 124, 49, 0.10));
          box-shadow: 0 14px 24px rgba(33, 89, 104, 0.12);
        }

        .pmsNavItemLabel,
        .pmsSubGroupLabel {
          color: #183645;
        }

        .pmsSubItem--depth2,
        .pmsSubItem--depth3 {
          border-color: rgba(24, 54, 69, 0.10);
          background: rgba(255, 255, 255, 0.70);
        }

        .pmsSubNav {
          border-left-color: rgba(24, 54, 69, 0.10);
        }

        .pmsNav {
          scrollbar-color: rgba(69, 78, 81, 0.22) transparent;
        }

        .pmsNav::-webkit-scrollbar-thumb {
          background: rgba(69, 78, 81, 0.18);
        }

        .pmsNav:hover::-webkit-scrollbar-thumb {
          background: rgba(69, 78, 81, 0.26);
        }

        .pmsSubGroup--active,
        .pmsSubItem--active {
          background: linear-gradient(180deg, rgba(33, 89, 104, 0.12), rgba(167, 124, 49, 0.08));
          border-color: rgba(33, 89, 104, 0.20);
          color: #183645;
        }

        .pmsMain,
        .pmsMain * {
          scrollbar-color: rgba(62, 86, 92, 0.34) rgba(91, 104, 100, 0.10);
        }

        .pmsMain *::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, rgba(62, 92, 103, 0.78), rgba(118, 128, 104, 0.62));
        }

        .pmsMain *::-webkit-scrollbar-track {
          background: rgba(91, 104, 100, 0.10);
        }
      `
    : "";

  const brandCss = `
    .pmsShell,
    .pmsMobileShell {
      --pilcore-blue: #223a7a;
      --pilcore-horizon: #3db6e9;
      --pilcore-connect: #63c1c7;
      --pilcore-ink: #172653;
      font-family: "Montserrat", sans-serif;
    }

    .pmsShell {
      color: #ffffff;
      background:
        radial-gradient(900px 460px at 86% -8%, rgba(61, 182, 233, 0.13), transparent 64%),
        radial-gradient(760px 440px at 18% 108%, rgba(99, 193, 199, 0.09), transparent 66%),
        linear-gradient(145deg, #0e1730 0%, #121e3d 52%, #172653 100%);
    }

    .pmsMobileShell {
      min-height: 100vh;
      background: linear-gradient(145deg, #0e1730 0%, #121e3d 52%, #172653 100%);
    }

    .pmsSidebar {
      width: 78px;
      border-color: rgba(61, 182, 233, 0.45);
      background: linear-gradient(180deg, #223a7a 0%, #1d326d 56%, #172653 100%);
      box-shadow: 0 20px 46px rgba(8, 18, 48, 0.3);
    }

    .pmsSidebar--open {
      width: 328px;
    }

    .pmsSidebar::before {
      background: linear-gradient(180deg, rgba(61, 182, 233, 0.11), transparent 26%);
      opacity: 1;
    }

    .pmsSidebarInner {
      padding: 10px 8px;
      gap: 8px;
    }

    .pmsBrand {
      min-height: 58px;
      padding: 8px 6px 12px;
      border: 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 0;
      background: transparent;
      box-shadow: none;
    }

    .pmsBrandMark {
      width: 38px;
      height: 38px;
      min-width: 38px;
      min-height: 38px;
      flex-basis: 38px;
      border-radius: 11px;
      border-color: rgba(61, 182, 233, 0.48);
      background: rgba(23, 38, 83, 0.46);
      color: var(--pilcore-horizon);
      box-shadow: none;
    }

    .pmsBrandLogo {
      filter: none;
    }

    .pmsBrandText {
      min-width: 0;
      flex: 1 1 auto;
    }

    .pmsSidebar--open .pmsBrandText {
      max-width: none;
    }

    .pmsBrandTitle {
      color: #ffffff;
      background: none;
      font-size: 0.88rem;
      font-weight: 600;
      letter-spacing: 0.015em;
      -webkit-text-fill-color: currentColor;
    }

    .pmsBrandSubtitle {
      color: var(--pilcore-horizon);
      font-size: 0.62rem;
      font-weight: 500;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .pmsBrandHotel,
    .pmsNavItemMeta,
    .pmsChevron,
    .pmsCurrencyControl__chevron,
    .pmsBrandLogout,
    .pmsBrandLogoutIcon {
      color: rgba(255, 255, 255, 0.64);
    }

    .pmsBrandLogout:hover {
      color: #ffffff;
      text-decoration-color: rgba(255, 255, 255, 0.72);
    }

    .pmsCurrencyControl__trigger {
      border-color: rgba(61, 182, 233, 0.28);
      color: rgba(255, 255, 255, 0.92);
      background: rgba(255, 255, 255, 0.055);
    }

    .pmsCurrencyControl__trigger:hover,
    .pmsCurrencyControl--open .pmsCurrencyControl__trigger {
      border-color: rgba(61, 182, 233, 0.62);
      background: rgba(61, 182, 233, 0.12);
    }

    .pmsCurrencyControl__code {
      color: var(--pilcore-horizon);
      background: rgba(23, 38, 83, 0.58);
    }

    .pmsCurrencyControl__options {
      border-color: rgba(61, 182, 233, 0.2);
      background: rgba(23, 38, 83, 0.72);
    }

    .pmsCurrencyControl__option {
      color: rgba(255, 255, 255, 0.7);
    }

    .pmsCurrencyControl__option:hover,
    .pmsCurrencyControl__option--active {
      color: #ffffff;
      border-color: rgba(61, 182, 233, 0.52);
      background: rgba(61, 182, 233, 0.16);
    }

    .pmsNavItem,
    .pmsSubItem,
    .pmsSubGroup {
      border-color: transparent;
      background: transparent;
      color: rgba(255, 255, 255, 0.88);
      box-shadow: none;
    }

    .pmsNavItem:hover,
    .pmsSubItem:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: transparent;
      transform: none;
      box-shadow: none;
    }

    .pmsNavItem {
      min-height: 48px;
      padding: 7px 8px;
      border-radius: 12px;
    }

    .pmsNavItem--active {
      background: linear-gradient(90deg, rgba(61, 182, 233, 0.26), rgba(99, 193, 199, 0.13));
      border-color: rgba(61, 182, 233, 0.44);
      color: #ffffff;
      box-shadow: inset 3px 0 0 var(--pilcore-horizon);
    }

    .pmsNavItemIcon {
      width: 34px;
      height: 34px;
      border-radius: 10px;
      border-color: transparent;
      background: transparent;
      color: rgba(255, 255, 255, 0.82);
      box-shadow: none;
    }

    .pmsNavItem--active .pmsNavItemIcon {
      border-color: var(--pilcore-horizon);
      background: rgba(23, 38, 83, 0.56);
      color: var(--pilcore-horizon);
      box-shadow: none;
    }

    .pmsNavItemLabel,
    .pmsSubGroupLabel {
      color: rgba(255, 255, 255, 0.94);
      font-weight: 500;
    }

    .pmsNavItem--active .pmsNavItemLabel {
      color: #ffffff;
      font-weight: 600;
    }

    .pmsNavItem--active .pmsNavItemMeta,
    .pmsNavItem--active .pmsChevron {
      color: rgba(255, 255, 255, 0.68);
    }

    .pmsNavItem--context {
      background: rgba(61, 182, 233, 0.07);
    }

    .pmsNavItem--context .pmsNavItemIcon,
    .pmsNavItem--context .pmsNavItemLabel {
      color: var(--pilcore-horizon);
    }

    .pmsSidebar:not(.pmsSidebar--open) .pmsNavItem--context {
      border-color: rgba(61, 182, 233, 0.34);
      background: rgba(61, 182, 233, 0.16);
      box-shadow: inset 3px 0 0 var(--pilcore-horizon);
    }

    .pmsSubNav {
      margin-left: 18px;
      border-left-color: rgba(61, 182, 233, 0.36);
      padding-left: 9px;
    }

    .pmsSubItem {
      margin: 2px 0;
      padding: 7px 9px;
      border: 0;
      border-radius: 8px;
      color: rgba(255, 255, 255, 0.72);
      font-size: 0.78rem;
    }

    .pmsSubItem--active {
      background: rgba(61, 182, 233, 0.15);
      color: #ffffff;
      box-shadow: inset 2px 0 0 var(--pilcore-horizon);
    }

    .pmsSubGroup {
      border: 0;
      border-radius: 9px;
      background: rgba(23, 38, 83, 0.2);
    }

    .pmsSubGroup--active {
      background: rgba(61, 182, 233, 0.08);
    }

    .pmsSubItem--depth2,
    .pmsSubItem--depth3 {
      border: 0;
    }

    .pmsPinButton {
      border-color: rgba(61, 182, 233, 0.34);
      background: rgba(23, 38, 83, 0.52);
      color: var(--pilcore-horizon);
      box-shadow: none;
    }

    .pmsPinButton:hover,
    .pmsPinButton--active {
      border-color: var(--pilcore-horizon);
      background: var(--pilcore-horizon);
      color: var(--pilcore-blue);
    }

    .pmsSidebarFooter {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      gap: 7px;
      padding-top: 9px;
      border-top: 1px solid rgba(255, 255, 255, 0.12);
    }

    .pmsSidebarLogout {
      width: 100%;
      min-height: 38px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 9px;
      border: 0;
      border-radius: 10px;
      color: rgba(255, 255, 255, 0.68);
      background: transparent;
      font: inherit;
      font-size: 0.72rem;
      font-weight: 500;
      cursor: pointer;
      transition: color 120ms ease, background 120ms ease;
    }

    .pmsSidebarLogout:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.08);
    }

    .pmsSidebarLogoutText {
      display: none;
    }

    .pmsSidebar--open .pmsSidebarLogout {
      justify-content: flex-start;
      padding: 0 11px;
    }

    .pmsSidebar--open .pmsSidebarLogoutText {
      display: inline;
    }

    .pmsNav {
      scrollbar-color: rgba(61, 182, 233, 0.38) transparent;
    }

    .pmsNav::-webkit-scrollbar-thumb,
    .pmsNav:hover::-webkit-scrollbar-thumb {
      background: rgba(61, 182, 233, 0.38);
    }

    .pmsMain,
    .pmsMain * {
      scrollbar-color: rgba(61, 182, 233, 0.64) rgba(34, 58, 122, 0.08);
    }

    .pmsMain *::-webkit-scrollbar-thumb {
      background: linear-gradient(180deg, var(--pilcore-horizon), var(--pilcore-connect));
    }

    .pmsMain *::-webkit-scrollbar-track {
      background: rgba(34, 58, 122, 0.08);
    }

    html[data-pms-theme="light"] .pmsShell,
    .pmsMobileShell--light {
      color: var(--pilcore-ink);
      background:
        radial-gradient(920px 420px at 92% -8%, rgba(61, 182, 233, 0.12), transparent 64%),
        radial-gradient(760px 420px at 18% 108%, rgba(99, 193, 199, 0.08), transparent 66%),
        linear-gradient(180deg, #f7fafc 0%, #f1f6fa 100%);
    }

    html[data-pms-theme="light"] .pmsSidebar {
      border-color: rgba(34, 58, 122, 0.14);
      background: linear-gradient(180deg, #ffffff 0%, #f5f9fc 100%);
      box-shadow: 0 20px 46px rgba(34, 58, 122, 0.13);
    }

    html[data-pms-theme="light"] .pmsSidebar::before {
      background:
        radial-gradient(360px 180px at 10% -4%, rgba(61, 182, 233, 0.13), transparent 68%),
        linear-gradient(180deg, rgba(99, 193, 199, 0.04), transparent 34%);
    }

    html[data-pms-theme="light"] .pmsBrand,
    html[data-pms-theme="light"] .pmsSidebarFooter {
      border-color: rgba(34, 58, 122, 0.11);
    }

    html[data-pms-theme="light"] .pmsBrandMark {
      border-color: var(--pilcore-horizon);
      background: var(--pilcore-blue);
    }

    html[data-pms-theme="light"] .pmsBrandTitle,
    html[data-pms-theme="light"] .pmsNavItemLabel,
    html[data-pms-theme="light"] .pmsSubGroupLabel {
      color: var(--pilcore-ink);
    }

    html[data-pms-theme="light"] .pmsBrandSubtitle {
      color: #258db9;
    }

    html[data-pms-theme="light"] .pmsBrandHotel,
    html[data-pms-theme="light"] .pmsNavItemMeta,
    html[data-pms-theme="light"] .pmsChevron,
    html[data-pms-theme="light"] .pmsCurrencyControl__chevron {
      color: rgba(23, 38, 83, 0.58);
    }

    html[data-pms-theme="light"] .pmsCurrencyControl__trigger {
      border-color: rgba(34, 58, 122, 0.13);
      color: var(--pilcore-ink);
      background: #ffffff;
      box-shadow: 0 5px 14px rgba(34, 58, 122, 0.05);
    }

    html[data-pms-theme="light"] .pmsCurrencyControl__trigger:hover,
    html[data-pms-theme="light"] .pmsCurrencyControl--open .pmsCurrencyControl__trigger {
      border-color: rgba(61, 182, 233, 0.62);
      background: rgba(61, 182, 233, 0.08);
    }

    html[data-pms-theme="light"] .pmsCurrencyControl__code {
      color: var(--pilcore-blue);
      background: rgba(61, 182, 233, 0.12);
    }

    html[data-pms-theme="light"] .pmsCurrencyControl__options {
      border-color: rgba(34, 58, 122, 0.12);
      background: #ffffff;
      box-shadow: 0 8px 20px rgba(34, 58, 122, 0.08);
    }

    html[data-pms-theme="light"] .pmsCurrencyControl__option {
      color: rgba(23, 38, 83, 0.7);
    }

    html[data-pms-theme="light"] .pmsCurrencyControl__option:hover,
    html[data-pms-theme="light"] .pmsCurrencyControl__option--active {
      color: var(--pilcore-blue);
      border-color: rgba(61, 182, 233, 0.38);
      background: rgba(61, 182, 233, 0.1);
    }

    html[data-pms-theme="light"] .pmsNavItem,
    html[data-pms-theme="light"] .pmsSubItem,
    html[data-pms-theme="light"] .pmsSubGroup {
      color: rgba(23, 38, 83, 0.78);
    }

    html[data-pms-theme="light"] .pmsNavItem:hover,
    html[data-pms-theme="light"] .pmsSubItem:hover {
      background: rgba(61, 182, 233, 0.09);
    }

    html[data-pms-theme="light"] .pmsNavItemIcon {
      color: rgba(34, 58, 122, 0.76);
    }

    html[data-pms-theme="light"] .pmsNavItem--active {
      border-color: var(--pilcore-blue);
      color: #ffffff;
      background: var(--pilcore-blue);
      box-shadow: 0 8px 20px rgba(34, 58, 122, 0.18);
    }

    html[data-pms-theme="light"] .pmsNavItem--active .pmsNavItemIcon {
      border-color: var(--pilcore-horizon);
      color: var(--pilcore-horizon);
      background: var(--pilcore-ink);
    }

    html[data-pms-theme="light"] .pmsNavItem--active .pmsNavItemLabel {
      color: #ffffff;
    }

    html[data-pms-theme="light"] .pmsNavItem--active .pmsNavItemMeta,
    html[data-pms-theme="light"] .pmsNavItem--active .pmsChevron {
      color: rgba(255, 255, 255, 0.66);
    }

    html[data-pms-theme="light"] .pmsNavItem--context {
      background: rgba(61, 182, 233, 0.07);
    }

    html[data-pms-theme="light"] .pmsNavItem--context .pmsNavItemIcon,
    html[data-pms-theme="light"] .pmsNavItem--context .pmsNavItemLabel {
      color: var(--pilcore-blue);
    }

    html[data-pms-theme="light"] .pmsSidebar:not(.pmsSidebar--open) .pmsNavItem--context {
      border-color: rgba(34, 58, 122, 0.24);
      background: rgba(61, 182, 233, 0.13);
      box-shadow: inset 3px 0 0 var(--pilcore-blue);
    }

    html[data-pms-theme="light"] .pmsSubNav {
      border-left-color: rgba(34, 58, 122, 0.18);
    }

    html[data-pms-theme="light"] .pmsSubItem {
      color: rgba(23, 38, 83, 0.68);
    }

    html[data-pms-theme="light"] .pmsSubItem--active {
      color: var(--pilcore-blue);
      background: rgba(61, 182, 233, 0.12);
      box-shadow: inset 2px 0 0 var(--pilcore-blue);
    }

    html[data-pms-theme="light"] .pmsSubGroup {
      background: rgba(34, 58, 122, 0.035);
    }

    html[data-pms-theme="light"] .pmsSubGroup--active {
      background: rgba(61, 182, 233, 0.08);
    }

    html[data-pms-theme="light"] .pmsPinButton {
      border-color: rgba(34, 58, 122, 0.14);
      color: var(--pilcore-blue);
      background: #ffffff;
    }

    html[data-pms-theme="light"] .pmsPinButton:hover,
    html[data-pms-theme="light"] .pmsPinButton--active {
      border-color: var(--pilcore-blue);
      color: #ffffff;
      background: var(--pilcore-blue);
    }

    html[data-pms-theme="light"] .pmsSidebarLogout {
      color: rgba(23, 38, 83, 0.62);
    }

    html[data-pms-theme="light"] .pmsSidebarLogout:hover {
      color: var(--pilcore-blue);
      background: rgba(61, 182, 233, 0.09);
    }

    html[data-pms-theme="light"] .pmsNav {
      scrollbar-color: rgba(34, 58, 122, 0.2) transparent;
    }

    html[data-pms-theme="light"] .pmsNav::-webkit-scrollbar-thumb,
    html[data-pms-theme="light"] .pmsNav:hover::-webkit-scrollbar-thumb {
      background: rgba(34, 58, 122, 0.2);
    }
  `;

  if (!canAccessPms) {
    return (
      <div
        style={{
          color: isLightTheme ? "#183645" : "white",
          textAlign: "center",
          marginTop: "4rem",
          fontSize: "1.2rem",
        }}
      >
        You don&apos;t have permission to access the PMS module.
      </div>
    );
  }

  if (isMobile) {
    return (
        <div className={`pmsMobileShell${isLightTheme ? " pmsMobileShell--light" : ""}`}>
        <style>{`${uiCss}\n${themeCss}\n${brandCss}`}</style>
        <Navbar />
        <main
          className="pmsMain pmsMain--mobile"
          style={{ paddingTop: "64px", paddingLeft: 8, paddingRight: 8 }}
        >
          <div className="pmsMobileCurrency">
            <CurrencyQuickSelector
              expanded
              label={sidebarCopy.displayCurrency || "Display currency"}
            />
          </div>
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="pmsShell">
      <style>{`${uiCss}\n${themeCss}\n${brandCss}`}</style>

      <aside
        className={`pmsSidebar ${isSidebarExpanded ? "pmsSidebar--open" : ""} ${
          isSidebarPinned ? "pmsSidebar--pinned" : ""
        }`}
        aria-label="PMS navigation"
        onMouseEnter={handleSidebarEnter}
        onMouseLeave={handleSidebarLeave}
      >
        <div className="pmsSidebarInner">
          <div className="pmsBrand" title={sidebarCopy.pmsPanel}>
            <div className="pmsBrandMark">
              <img
                className="pmsBrandLogo"
                src={`${process.env.PUBLIC_URL}/Isotipo_Pilcore_Blanco.svg`}
                alt="Pilcore"
              />
            </div>
            <div className="pmsBrandText">
              <div className="pmsBrandTitle">Pilcore</div>
              <div className="pmsBrandSubtitle">PMS</div>
              <div
                className="pmsBrandHotel"
                title={
                  businessUnitName || currentUser?.business_unit_name || commonCopy.businessUnit
                }
              >
                {businessUnitName || currentUser?.business_unit_name || commonCopy.businessUnit}
              </div>
            </div>
            {isSidebarExpanded ? (
              <button
                type="button"
                className={`pmsPinButton ${isSidebarPinned ? "pmsPinButton--active" : ""}`}
                onClick={handlePinToggle}
                title={isSidebarPinned ? sidebarCopy.unpinSidebar : sidebarCopy.pinSidebar}
                aria-pressed={isSidebarPinned}
                aria-label={isSidebarPinned ? sidebarCopy.unpinSidebar : sidebarCopy.pinSidebar}
              >
                <FiMapPin size={14} />
              </button>
            ) : null}
          </div>

          <nav ref={navScrollRef} className="pmsNav">
            {menuItems.map((item) => {
              if (item.children?.length) {
                const isActive = activeMenuKey === item.key;
                const isOpen = openSection === item.key;
                const Icon = item.Icon;

                return (
                  <div
                    key={item.key}
                    ref={(node) => setSectionRef(item.key, node)}
                    className="pmsNavSection"
                  >
                    <button
                      type="button"
                      className={`pmsNavItem ${isActive ? "pmsNavItem--context" : ""}`}
                      aria-expanded={isOpen}
                      onClick={() => setOpenSection((prev) => (prev === item.key ? null : item.key))}
                      title={item.label}
                    >
                      <span className="pmsNavItemIcon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <span className="pmsNavItemText">
                        <span className="pmsNavItemLabel">{item.label}</span>
                        <span className="pmsNavItemMeta">{item.meta}</span>
                      </span>
                      <span className="pmsChevron" aria-hidden="true">
                        <FiChevronDown size={16} />
                      </span>
                    </button>

                    <div className={`pmsSubNav ${isOpen ? "pmsSubNav--open" : ""}`}>
                      {item.children.map((child) =>
                        renderSubNode(child, 1, item.key, item.key)
                      )}
                    </div>
                  </div>
                );
              }

              const Icon = item.Icon;
              const isActive = activeMenuKey === item.key;
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  title={item.label}
                  aria-current={isActive ? "page" : undefined}
                  className={`pmsNavItem ${isActive ? "pmsNavItem--active" : ""}`}
                >
                  <span className="pmsNavItemIcon" aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  <span className="pmsNavItemText">
                    <span className="pmsNavItemLabel">{item.label}</span>
                    <span className="pmsNavItemMeta">{item.meta}</span>
                  </span>
                </Link>
              );
            })}
          </nav>

          <div className="pmsSidebarFooter">
            <CurrencyQuickSelector
              expanded={isSidebarExpanded}
              label={sidebarCopy.displayCurrency || "Display currency"}
            />
            {!demoModeActive ? (
              <button
                type="button"
                className="pmsSidebarLogout"
                onClick={handleLogout}
                title={commonCopy.logout}
                aria-label={commonCopy.logout}
              >
                <FiLogOut size={16} aria-hidden="true" />
                <span className="pmsSidebarLogoutText">{commonCopy.logout}</span>
              </button>
            ) : null}
          </div>
        </div>
      </aside>

      <main className="pmsMain">
        <Outlet />
      </main>
    </div>
  );
}

export default PmsPanel;
